import { useId, useMemo, useState } from "react";
import type { MouseEvent } from "react";
import { PageHeader } from "@/components/PageHeader";
import { StateMessage } from "@/components/StateMessage";
import { EntryRow } from "@/components/EntryRow";
import { useBooksActivity, type BooksActivityFilters } from "@/hooks/useBooksActivity";
import { useReferenceData } from "@/hooks/useReferenceData";
import { formatDateShort, formatSignedMoney } from "@/lib/format";
import type { Account, BooksActivityEntry, Entry, EntrySource, Vendor } from "@/api/types";
import "./ReportsActivity.css";

/** What `entry.source`'s own CHECK constraint accepts (BooksActivityEndpoints.cs), spelled
 *  as the lowercase values the query string takes. Moved verbatim from `Books.tsx`'s own
 *  `BooksActivityPanel` (ef-056-5b, ADR 0010 §1) — same list, same flag: `crop` selects
 *  but 400s server-side until `AllowedSources` widens too. */
const SOURCE_OPTIONS: Array<{ value: string; label: string }> = [
  { value: "manual", label: "Manual" },
  { value: "receipt", label: "Receipt" },
  { value: "livestock", label: "Livestock" },
  { value: "payroll", label: "Payroll" },
  { value: "crop", label: "Crop" },
];

/**
 * Is this a source `EntryRow`'s own three-member assumption already knows how to render?
 * `BooksActivityEntry.source` is a raw string (ef-042-0a — see api/types.ts's own
 * remarks), so this is a runtime check, not a type-system one; `Payroll` (and any future
 * source `entry.source`'s CHECK constraint grows before `EntrySource` does) answers false
 * and falls back to the minimal row below rather than being force-fit into it. Moved
 * verbatim from `Books.tsx`.
 */
function isKnownEntrySource(source: string): source is EntrySource {
  return source === "Manual" || source === "Receipt" || source === "Livestock" || source === "Crop";
}

/**
 * `/reports/activity` — ADR 0010 §1: Books' old cross-account Activity tab moves here,
 * a `reportRegistry.ts` picker entry (`@report-registry-anchor`, ef-056-0a). Same
 * overflow rule ADR 0009 §2 already used for Approved Hours — a cross-account view isn't
 * one instrument's register, so it lives among the reports, not on Books.
 *
 * ef-056-5b (this job) replaces the ef-056-0a stub with the real page: `useBooksActivity`
 * + the same filter form/row rendering the old `BooksActivityPanel` already had — a
 * relocation, not a rewrite (`reports-activity.spec.ts`'s own remarks). "All reports"
 * below is the only way back, same `ReportsCashFlow.tsx`/`ReportsTrialBalance.tsx`
 * pattern every no-tab-strip report page already uses (ADR 0009 §6).
 */
export function ReportsActivity() {
  const { vendors, accounts } = useReferenceData();

  // ef-056 holdback #11 — read once, on mount, the SAME "seed the first request from the
  // URL" contract `Labour.tsx`'s own `initialRange` already uses: a deep link (a saved
  // report, a cross-page "view this in Activity" link) must land on the filtered feed on
  // FIRST PAINT, not the unfiltered default with the query string sitting unread in the
  // address bar.
  const initialFilters = useMemo<BooksActivityFilters>(() => {
    const params = new URLSearchParams(window.location.search);
    const filters: BooksActivityFilters = {};
    const sourceParam = params.get("source");
    const accountParam = params.get("account");
    const fromParam = params.get("from");
    const toParam = params.get("to");
    if (sourceParam) filters.source = sourceParam;
    if (accountParam) filters.accountId = accountParam;
    if (fromParam) filters.from = fromParam;
    if (toParam) filters.to = toParam;
    return filters;
  }, []);

  const { entries, status, error, load } = useBooksActivity(initialFilters);

  const vendorById = useMemo(
    () => new Map<string, Vendor>(vendors.map((v) => [v.id, v] as const)),
    [vendors],
  );
  const accountById = useMemo(
    () => new Map<string, Account>(accounts.map((a) => [a.id, a] as const)),
    [accounts],
  );

  const sourceFieldId = useId();
  const accountFieldId = useId();
  const fromId = useId();
  const toId = useId();

  const [source, setSource] = useState(initialFilters.source ?? "");
  const [account, setAccount] = useState(initialFilters.accountId ?? "");
  const [from, setFrom] = useState(initialFilters.from ?? "");
  const [to, setTo] = useState(initialFilters.to ?? "");

  const rangeIsBackwards = from !== "" && to !== "" && to < from;
  const canSubmit = !rangeIsBackwards && status !== "loading";

  function applyFilters(): void {
    if (!canSubmit) return;
    const filters: BooksActivityFilters = {};
    if (source) filters.source = source;
    if (account) filters.accountId = account;
    if (from) filters.from = from;
    if (to) filters.to = to;
    void load(filters);

    // Keeps the address bar a shareable/bookmarkable link to THIS filter set — same
    // `pushState`-without-navigation discipline `handleAllReportsClick` below already
    // uses, but `replaceState` (no back-button entry per Apply click).
    const params = new URLSearchParams();
    if (source) params.set("source", source);
    if (account) params.set("account", account);
    if (from) params.set("from", from);
    if (to) params.set("to", to);
    const query = params.toString();
    window.history.replaceState({}, "", query ? `/reports/activity?${query}` : "/reports/activity");
  }

  function handleAllReportsClick(event: MouseEvent<HTMLAnchorElement>): void {
    event.preventDefault();
    // Self-contained, same device ReportsCashFlow.tsx's own handler uses: pushState
    // doesn't fire popstate on its own, so App.tsx's existing listener is told directly.
    window.history.pushState({}, "", "/reports");
    window.dispatchEvent(new PopStateEvent("popstate"));
  }

  return (
    <main className="reports-activity">
      <PageHeader title="Activity" subtitle="Every entry, any account, any source." />

      <a href="/reports" className="reports-activity__all-reports" onClick={handleAllReportsClick}>
        ← All reports
      </a>

      <section
        className="reports-activity__panel"
        aria-labelledby="reports-activity-heading"
        // Ground truth for a tester, rules/ui-breadcrumbs.md §1 — read off the DOM rather
        // than inferred from sentence text.
        data-state={status === "ready" && entries.length === 0 ? "empty" : status}
      >
        <h2 id="reports-activity-heading" className="reports-activity__heading">
          Activity
        </h2>

        <form
          className="reports-activity__filters"
          onSubmit={(event) => {
            event.preventDefault();
            applyFilters();
          }}
        >
          <div className="reports-activity__field">
            <label className="reports-activity__label" htmlFor={sourceFieldId}>Source</label>
            <select
              id={sourceFieldId}
              className="reports-activity__input"
              value={source}
              onChange={(event) => setSource(event.target.value)}
            >
              <option value="">All sources</option>
              {SOURCE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>

          <div className="reports-activity__field">
            <label className="reports-activity__label" htmlFor={accountFieldId}>Account</label>
            <select
              id={accountFieldId}
              className="reports-activity__input"
              value={account}
              onChange={(event) => setAccount(event.target.value)}
            >
              <option value="">All accounts</option>
              {accounts.map((a) => (
                <option key={a.id} value={a.id}>{a.number} — {a.name}</option>
              ))}
            </select>
          </div>

          <div className="reports-activity__field">
            <label className="reports-activity__label" htmlFor={fromId}>From</label>
            <input
              id={fromId}
              className="reports-activity__input"
              type="date"
              value={from}
              onChange={(event) => setFrom(event.target.value)}
            />
          </div>

          <div className="reports-activity__field">
            <label className="reports-activity__label" htmlFor={toId}>To</label>
            <input
              id={toId}
              className="reports-activity__input"
              type="date"
              value={to}
              aria-invalid={rangeIsBackwards || undefined}
              onChange={(event) => setTo(event.target.value)}
            />
          </div>

          <button type="submit" className="reports-activity__apply" disabled={!canSubmit}>
            Apply
          </button>
        </form>

        {rangeIsBackwards && (
          <p className="reports-activity__field-error">
            The end of the window cannot be before its start.
          </p>
        )}

        {status === "loading" && <StateMessage tone="loading" title="Loading activity…" />}

        {status === "error" && (
          <StateMessage
            tone="error"
            title="Could not load the activity feed."
            detail={error ?? undefined}
          />
        )}

        {status === "ready" && entries.length === 0 && (
          <StateMessage
            tone="empty"
            title="No activity in this window."
            detail="Try widening the date range, or clearing a filter."
          />
        )}

        {status === "ready" && entries.length > 0 && (
          <ul className="reports-activity__list" aria-label="Books activity">
            {entries.map((entry) => (
              <ReportsActivityRow
                key={entry.id}
                entry={entry}
                vendor={entry.vendorId ? vendorById.get(entry.vendorId) : undefined}
                account={entry.accountId ? accountById.get(entry.accountId) : undefined}
              />
            ))}
          </ul>
        )}
      </section>
    </main>
  );
}

function ReportsActivityRow({
  entry,
  vendor,
  account,
}: {
  entry: BooksActivityEntry;
  vendor: Vendor | undefined;
  account: Account | undefined;
}) {
  if (isKnownEntrySource(entry.source)) {
    const asEntry: Entry = {
      id: entry.id,
      entityId: entry.entityId,
      vendorId: entry.vendorId,
      accountId: entry.accountId,
      paymentMethodId: entry.paymentMethodId,
      amount: entry.amount,
      memo: entry.memo,
      occurredOn: entry.occurredOn,
      source: entry.source,
      createdAt: entry.createdAt,
      receiptImageId: entry.receiptImageId,
    };
    return <EntryRow entry={asEntry} vendor={vendor} account={account} isNew={false} />;
  }

  // A source EntryRow's own sourceLabel has never heard of (Payroll today, and any
  // future addition to entry.source's CHECK constraint the EntrySource enum has not
  // caught up with) — a minimal row of our own, reusing EntryRow's own CSS classes for
  // visual consistency, rather than force-fitting a label EntryRow would get wrong.
  const tone = account?.kind === "Income" ? "pos" : "neg";
  return (
    <li className="entry-row">
      <span className="entry-row__avatar entry-row__avatar--neutral" aria-hidden="true">
        {entry.source.slice(0, 2).toUpperCase()}
      </span>
      <span className="entry-row__meta">
        <span className="entry-row__vendor">{account?.name ?? "Uncategorized"}</span>
        <span className="entry-row__sub">
          {entry.source} · {formatDateShort(entry.occurredOn)}
        </span>
      </span>
      <span className="entry-row__receipt-slot" />
      <span className={`entry-row__amount entry-row__amount--${tone}`}>
        {formatSignedMoney(entry.amount, account?.kind)}
      </span>
    </li>
  );
}
